import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { MuscleId } from './muscles'
import type { StrainEvent } from './recovery'
import { computePeakFromSets, type WorkoutSet } from './volume'

export type { WorkoutSet }

export interface WorkoutRecord {
  id: string
  name: string
  at: number
  source: 'qr' | 'manual'
  machineId?: string
  qrPayload?: string
  muscles: MuscleId[]
  /** Per-set reps + weight */
  sets: WorkoutSet[]
  /** Auto-derived 1-10 from load vs personal history */
  intensity: number
  notes?: string
}

interface MyoFadeDB extends DBSchema {
  workouts: {
    key: string
    value: WorkoutRecord
    indexes: { 'by-date': number }
  }
}

const DB_NAME = 'myofade'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase<MyoFadeDB>> | null = null

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<MyoFadeDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore('workouts', { keyPath: 'id' })
        store.createIndex('by-date', 'at')
      },
    })
  }
  return dbPromise
}

/** Test helper - closes and forgets the shared DB connection. */
export async function resetDbForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise
    db.close()
  }
  dbPromise = null
  if (typeof indexedDB !== 'undefined') {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME)
      const done = () => resolve()
      req.onsuccess = done
      req.onerror = done
      req.onblocked = done
    })
  }
}

export async function addWorkout(workout: Omit<WorkoutRecord, 'id'> & { id?: string }): Promise<WorkoutRecord> {
  const db = await getDb()
  const record: WorkoutRecord = {
    ...workout,
    id: workout.id ?? crypto.randomUUID(),
  }
  await db.put('workouts', record)
  return record
}

export async function listWorkouts(): Promise<WorkoutRecord[]> {
  const db = await getDb()
  const all = await db.getAllFromIndex('workouts', 'by-date')
  return all.reverse()
}

/** Newest-first page for infinite scroll. */
export async function listWorkoutsPage(opts: {
  /** Inclusive max `at` (ms). Omit for the newest page. */
  beforeAt?: number
  /** Skip rows already shown (needed when several share the same `at`). */
  excludeIds?: Iterable<string>
  limit?: number
} = {}): Promise<WorkoutRecord[]> {
  const db = await getDb()
  const limit = Math.max(1, opts.limit ?? 40)
  const exclude = new Set(opts.excludeIds ?? [])
  const tx = db.transaction('workouts', 'readonly')
  const index = tx.store.index('by-date')
  const range =
    opts.beforeAt === undefined ? null : IDBKeyRange.upperBound(opts.beforeAt, false)
  const results: WorkoutRecord[] = []
  let cursor = await index.openCursor(range, 'prev')
  while (cursor && results.length < limit) {
    if (!exclude.has(cursor.value.id)) results.push(cursor.value)
    cursor = await cursor.continue()
  }
  await tx.done
  return results
}

export async function countWorkouts(): Promise<number> {
  const db = await getDb()
  return db.count('workouts')
}

export async function deleteWorkout(id: string): Promise<void> {
  const db = await getDb()
  await db.delete('workouts', id)
}

export async function clearWorkouts(): Promise<void> {
  const db = await getDb()
  await db.clear('workouts')
}

/** Replace all workouts (used by backup restore). */
export async function replaceWorkouts(workouts: WorkoutRecord[]): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('workouts', 'readwrite')
  await tx.store.clear()
  for (const row of workouts) {
    await tx.store.put(row)
  }
  await tx.done
}

/** Same exercise = matching machineId, else case-insensitive name. */
export function isSameExercise(
  a: Pick<WorkoutRecord, 'name' | 'machineId'>,
  b: Pick<WorkoutRecord, 'name' | 'machineId'>,
): boolean {
  if (a.machineId && b.machineId) return a.machineId === b.machineId
  return a.name.trim().toLowerCase() === b.name.trim().toLowerCase()
}

export function priorSetsForExercise(
  history: WorkoutRecord[],
  exercise: Pick<WorkoutRecord, 'name' | 'machineId'>,
): WorkoutSet[][] {
  return history.filter((w) => isSameExercise(w, exercise)).map((w) => w.sets)
}

/** Flatten workouts → strain events for the recovery engine */
export function workoutsToStrainEvents(workouts: WorkoutRecord[]): StrainEvent[] {
  const events: StrainEvent[] = []
  for (const w of workouts) {
    const peak = computePeakFromSets(w.sets, w.intensity)
    for (const muscleId of w.muscles) {
      events.push({ muscleId, at: w.at, peak })
    }
  }
  return events
}
